import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, Loader2, CheckCircle2, ArrowRight, ShieldCheck, Clock, ThumbsUp } from "lucide-react";
import { COMPANY, ASSETS, SERVICE_OPTIONS, TRUST_BADGES } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

const EMPTY = { name: "", phone: "", email: "", address: "", service: "", message: "", sms_consent: false };

export default function Estimate() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  useScrollReveal();
  useSEO({
    title: "Free Estimate - Custom Screen & Pool Enclosures in Melbourne, FL | Miller & Miller",
    description: "Get a fast, free, no-obligation estimate for pool cages, patio screens, rescreens & more from Miller & Miller Services LLC. We respond within one business day.",
    keywords: ["pool enclosure estimate", "pool cage quote", "free screen enclosure estimate Melbourne FL"],
    path: "/estimate",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast({ title: "A few details missing", description: "Please add your name, phone, and email." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/estimate`, form);
      setDone(true);
      setForm(EMPTY);
      toast({ title: "Estimate request sent!", description: "We'll follow up within one business day." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  const perks = [
    { icon: ShieldCheck, text: "No obligation, ever" },
    { icon: ThumbsUp, text: "Real humans, honest pricing" },
    { icon: Clock, text: "Response within 1 business day" },
  ];

  return (
    <div>
      <section className="relative pt-24 pb-32 md:pt-40 md:pb-40 bg-brand-navy text-white overflow-hidden">
        <video className="absolute inset-0 h-full w-full object-cover opacity-30" autoPlay muted loop playsInline poster={ASSETS.pool1}>
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/90 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">Free Estimate</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-2xl">Get Your Free, No-Obligation Estimate</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Tell us a little about your project and we'll follow up within one business day with honest pricing and a plan.
          </p>
        </div>
      </section>

      <section className="bg-white pb-20">
        <div className="container-x -mt-28 relative z-10 grid lg:grid-cols-[1.4fr_0.9fr] gap-8 items-start">
          {/* Form card */}
          <div className="reveal rounded-3xl bg-white p-7 md:p-9 shadow-card ring-1 ring-brand-mist">
            {done ? (
              <div className="text-center py-12">
                <CheckCircle2 className="h-20 w-20 text-brand-ocean mx-auto" />
                <h3 className="mt-4 text-2xl font-heading font-extrabold text-brand-navy">Request Received!</h3>
                <p className="mt-2 text-brand-slate max-w-md mx-auto">Thanks for reaching out to Miller & Miller Services. Our team will follow up within one business day.</p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold transition-colors">
                    <Phone className="h-4 w-4" /> {COMPANY.phone}
                  </a>
                  <Link to="/gallery" className="inline-flex items-center gap-2 h-12 px-7 rounded-md ring-1 ring-brand-slate/25 hover:ring-brand-ocean text-brand-navy font-heading font-bold transition-all">
                    Browse Gallery <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h2 className="text-2xl font-heading font-extrabold text-brand-navy">Request My Free Estimate</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="e-name" className="font-semibold text-brand-navy">Full Name *</Label>
                    <Input id="e-name" value={form.name} onChange={update("name")} placeholder="Your full name" className="mt-1.5 h-12" />
                  </div>
                  <div>
                    <Label htmlFor="e-phone" className="font-semibold text-brand-navy">Phone *</Label>
                    <Input id="e-phone" value={form.phone} onChange={update("phone")} placeholder="(321) 000-0000" className="mt-1.5 h-12" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="e-email" className="font-semibold text-brand-navy">Email *</Label>
                    <Input id="e-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="mt-1.5 h-12" />
                  </div>
                  <div>
                    <Label htmlFor="e-address" className="font-semibold text-brand-navy">Service Address</Label>
                    <Input id="e-address" value={form.address} onChange={update("address")} placeholder="Street, City, FL" className="mt-1.5 h-12" />
                  </div>
                </div>
                <div>
                  <Label className="font-semibold text-brand-navy">Service Needed</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger className="mt-1.5 h-12">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="e-msg" className="font-semibold text-brand-navy">Tell Us About Your Project</Label>
                  <Textarea id="e-msg" value={form.message} onChange={update("message")} placeholder="Describe your space, goals, timeline..." rows={4} className="mt-1.5" />
                </div>
                <label htmlFor="e-sms-consent" className="flex items-start gap-3 cursor-pointer rounded-xl bg-brand-mist/40 ring-1 ring-brand-mist p-4">
                  <input
                    id="e-sms-consent"
                    type="checkbox"
                    checked={form.sms_consent}
                    onChange={(e) => setForm((f) => ({ ...f, sms_consent: e.target.checked }))}
                    data-testid="estimate-sms-consent"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-brand-slate/40 text-brand-ocean focus:ring-brand-ocean accent-brand-ocean cursor-pointer"
                  />
                  <span className="text-xs leading-relaxed text-brand-slate">
                    By checking this box, you agree to receive SMS messages from Miller &amp; Miller Services LLC
                    related to estimate scheduling, appointment reminders, and service updates. You may reply STOP
                    to opt out at any time. Reply HELP to 321-608-0255 for assistance. Messages and data rates may
                    apply. Message frequency will vary. Learn more on our{" "}
                    <Link to="/privacy-policy" className="font-bold text-brand-ocean hover:underline">Privacy Policy</Link> page and{" "}
                    <Link to="/terms-conditions" className="font-bold text-brand-ocean hover:underline">Terms &amp; Conditions</Link>.
                  </span>
                </label>
                <Button type="submit" disabled={loading} className="w-full h-14 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold text-base shadow-gold">
                  {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Sending...</> : <>Request My Free Estimate <ArrowRight className="h-5 w-5 ml-1" /></>}
                </Button>
                <p className="text-xs text-brand-slate text-center">
                  By submitting, you agree to be contacted about your request. We'll never share your info.
                </p>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="reveal space-y-5 lg:pt-6">
            <div className="rounded-2xl bg-brand-navy text-white p-7">
              <h3 className="font-heading font-extrabold text-xl">Prefer to call?</h3>
              <a href={COMPANY.phoneHref} className="mt-3 flex items-center gap-3 text-brand-gold2 hover:text-brand-gold transition-colors">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/10"><Phone className="h-5 w-5" /></span>
                <span className="text-2xl font-heading font-extrabold">{COMPANY.phone}</span>
              </a>
              <p className="text-white/60 text-sm mt-3">{COMPANY.hours}</p>
            </div>

            <div className="rounded-2xl bg-brand-mist/60 p-7 ring-1 ring-brand-mist">
              <ul className="space-y-3.5">
                {perks.map((p) => {
                  const Icon = p.icon;
                  return (
                    <li key={p.text} className="flex items-center gap-3 text-brand-navy font-semibold">
                      <Icon className="h-5 w-5 text-brand-ocean shrink-0" /> {p.text}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-2xl bg-white p-7 ring-1 ring-brand-mist shadow-card">
              <div className="flex flex-wrap gap-2">
                {TRUST_BADGES.map((b) => (
                  <span key={b} className="text-xs font-heading font-bold bg-brand-mist text-brand-navy rounded-full px-3 py-1.5">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
